import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { CouponService } from "../../services/Coupon.service";
import { handleApiError } from "../../utils/handleApiError";
import { useI18n } from "../../i18n";

const STATUS_STYLE = {
  active: "bg-green-100 text-green-700",
  redeemed: "bg-blue-100 text-blue-700",
  expired: "bg-gray-100 text-gray-500",
  revoked: "bg-red-100 text-red-600",
};

const MyCoupons = () => {
  const { t, tr, formatDate } = useI18n();
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);

  const formatDiscount = (c) =>
    c.discount_type === "percent"
      ? t("discount.percent", { value: parseFloat(c.value) })
      : t("discount.amount", { value: parseFloat(c.value) });

  useEffect(() => {
    (async () => {
      try {
        const data = await CouponService.my();
        setCoupons(data.coupons || []);
      } catch (error) {
        toast.error(handleApiError(error, t));
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="max-w-3xl mx-auto page pt-6">
      <h1 className="text-2xl sm:text-3xl font-extrabold text-white mb-5">
        {t("coupons.title")}
      </h1>

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-12 h-12 border-4 border-white border-t-transparent rounded-full animate-spin" />
        </div>
      ) : coupons.length === 0 ? (
        <div className="bg-white rounded-3xl shadow-xl p-10 text-center">
          <p className="text-5xl mb-4">🎫</p>
          <p className="text-xl font-bold text-gray-800 mb-2">
            {t("coupons.empty")}
          </p>
          <p className="text-gray-500 mb-6">{t("coupons.empty_desc")}</p>
          <Link
            to="/stores"
            className="inline-block bg-gradient-to-r from-indigo-500 to-fuchsia-600 text-white font-bold px-8 py-3 rounded-xl shadow hover:scale-105 transition"
          >
            {t("coupons.browse")}
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          {coupons.map((c) => {
            const isActive = c.status === "active";
            const cls = STATUS_STYLE[c.status] || STATUS_STYLE.active;
            return (
              <div
                key={c.code}
                className={`bg-white rounded-2xl shadow-lg p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4 border-l-8 ${
                  isActive ? "border-green-400" : "border-gray-200 opacity-80"
                }`}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-2xl font-mono font-extrabold text-indigo-600 tracking-wider">
                      {c.code}
                    </span>
                    <span
                      className={`text-xs font-bold px-3 py-1 rounded-full ${cls}`}
                    >
                      {t(`coupons.status.${c.status}`)}
                    </span>
                  </div>
                  <p className="text-lg font-bold text-green-600 mt-1">
                    {formatDiscount(c)}
                  </p>
                  <p className="text-sm text-gray-500">
                    {c.merchant?.name && <>🏪 {tr(c.merchant.name)} · </>}
                    {c.expires_at &&
                      t("coupons.expires", {
                        date: formatDate(c.expires_at, {
                          dateStyle: "medium",
                          timeStyle: "short",
                        }),
                      })}
                  </p>
                </div>

                {isActive && (
                  <button
                    onClick={() => {
                      navigator.clipboard?.writeText(c.code);
                      toast.success(t("common.copied"));
                    }}
                    className="shrink-0 bg-indigo-50 text-indigo-600 font-semibold px-5 py-2.5 rounded-xl hover:bg-indigo-100 transition text-sm"
                  >
                    {t("common.copy")}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MyCoupons;
